import {useReachGoalCallback} from '@yandex-delivery/blocks'
import {type MouseEvent, useCallback} from 'react'

import {BannerView, METRIKA_GOALS} from '../PromoBanner.consts'
import {usePromoBannerContext} from '../PromoBanner.context'
import {usePromoBannerAppLinkByVew} from '../PromoBanner.hooks'
import type {Props} from './PromoBannerButton.types'

export const useDownloadAppCallback = (bannerView: BannerView, promoCode?: string, deeplink?: string) => {
  const appLink = usePromoBannerAppLinkByVew(bannerView, promoCode, deeplink)

  return useCallback(() => {
    window.open(appLink, '_blank')
  }, [appLink])
}

export const useActionButton = ({
  action,
  text,
  promoCode,
  bannerView = BannerView.WITH_LEAD_BUTTON,
  deeplink,
}: Props) => {
  const {onClosePopupScreen, onOpenPopupScreen} = usePromoBannerContext()
  const appLink = usePromoBannerAppLinkByVew(bannerView, promoCode, deeplink)
  const reachGoalCallback = useReachGoalCallback()

  const getApplicationCallback = useDownloadAppCallback(bannerView, promoCode, deeplink)

  const goToAppAction = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      window.open(appLink, '_blank')
      reachGoalCallback(METRIKA_GOALS.COPIED_COUPON_BANNER_GO_TO_APP_CLICK)
      onClosePopupScreen()
    },
    [appLink, onClosePopupScreen, reachGoalCallback],
  )

  const closeAction = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      onClosePopupScreen()
    },
    [onClosePopupScreen],
  )

  const copyAction = useCallback(
    async (event: MouseEvent) => {
      event.stopPropagation()

      await window.navigator.clipboard.writeText(text ?? '')
      reachGoalCallback(METRIKA_GOALS.INSTALL_BANNER_WITH_PROMO_PROMO_CODE_CLICK)
      onOpenPopupScreen()
    },
    [reachGoalCallback, onOpenPopupScreen, text],
  )

  const getAppAction = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()

      getApplicationCallback()
    },
    [getApplicationCallback],
  )

  switch (action) {
    case 'close':
      return closeAction
    case 'go-to-app':
      return goToAppAction
    case 'copy-coupon':
      return copyAction
    case 'get-app':
    default:
      return getAppAction
  }
}
